<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { coreServiceRegistry } from "@core/services/register"
import { formatIdSource } from "@core/helpers"
import type { BaseSummaryLine } from "@core/components/base/summary/BaseSummary.vue"
import type { OwnerOutput } from "@core/composables"
import type { Address } from "@types"
import type { ConsentRequestSignResponse } from "@core/services/consentrequest.service"

// ================================
// TYPES & INTERFACES
// ================================

type InvitationResponse = ConsentRequestSignResponse

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /** Identifiant Mongo de la source (compteur) */
        sourceMongoId: string
        /** Adresse du bâtiment (affichage récap) */
        address: Address | Record<string, unknown>
        /** Numéro PDL / PCE affiché */
        idsource: string
        /** Type pdl | pce */
        type?: string
        /** Titulaire du consentement (destinataire de la demande) */
        owner: OwnerOutput
    }>(),
    {
        type: "pdl",
    }
)

const emit = defineEmits<{
    back: []
    sent: [payload: { _id_consentrequest: string; url: string }]
}>()

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    consentRequest?: typeof coreServiceRegistry.consentRequest
}>("services")
const consentRequestService =
    injectedServices?.consentRequest ?? coreServiceRegistry.consentRequest

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Demande de prolongement du consentement",
            subtitle:
                "Un e-mail sera envoyé au titulaire pour qu'il prolonge son consentement.",
            explain:
                "Le titulaire du compteur recevra une demande par e-mail. Il pourra accepter le prolongement depuis le lien reçu.",
            generate: "Générer la demande",
            back: "Retour",
            missing_user_id:
                "Impossible d'envoyer la demande : identifiant du titulaire manquant.",
            field_titulaire_pro: "Titulaire (professionnel)",
            field_titulaire_particular: "Titulaire (particulier)",
            field_company: "Entreprise",
            field_address: "Adresse",
            field_email: "E-mail",
            field_idsource_pdl: "Numéro PDL",
            field_idsource_pce: "Numéro PCE",
        },
    },
})

// ================================
// STATE
// ================================

const invitationResult = ref<InvitationResponse | null>(null)

// ================================
// FETCH
// ================================

async function sendRequestFn(): Promise<void> {
    const userId = props.owner.user._id?.trim()
    if (!userId) return
    const result = await consentRequestService.createConsentRequestExtend(
        props.sourceMongoId,
        userId,
        { mail: false }
    )
    invitationResult.value = result
}

const {
    fetch: fetchSend,
    isFetching,
    hasError,
    error: fetchError,
    isSuccess,
} = useFetchWrapper(sendRequestFn)

const getErrorMessage = computed(() => {
    const err = fetchError.value
    if (!err) return ""
    return (
        (err as Error & { response?: { data?: { message?: string } } })
            ?.response?.data?.message ??
        err.message ??
        "Une erreur est survenue."
    )
})

watch(isSuccess, (success) => {
    if (success && invitationResult.value) {
        const res = invitationResult.value
        const id = res._id ?? ""
        const path = `/demande/prolongement-consentement/${id}`
        const url =
            res.url?.trim() ||
            (typeof window !== "undefined"
                ? `${window.location.origin}${path}`
                : path)
        emit("sent", {
            _id_consentrequest: id,
            url,
        })
        invitationResult.value = null
    }
})

// ================================
// COMPUTED
// ================================

/** true si l’API peut être appelée (titulaire avec _id connu). */
const getCanSubmit = computed(
    () => !!props.owner.user._id?.trim() && getFields.value.length > 0
)

const getFields = computed<BaseSummaryLine[]>(() => {
    const ownerLabel = props.owner?.company?.name
        ? t("field_titulaire_pro")
        : t("field_titulaire_particular")
    const lines: BaseSummaryLine[] = [
        {
            label: ownerLabel,
            value: `${props.owner?.user?.firstname ?? ""} ${props.owner?.user?.lastname ?? ""}`.trim(),
        },
        {
            label: t("field_address"),
            value:
                (props.address as Address)?.inline_name ??
                (props.address as Address)?.street ??
                "",
        },
        {
            label: t("field_email"),
            value: props.owner?.user?.username ?? "",
        },
        {
            label:
                props.type === "pce"
                    ? t("field_idsource_pce")
                    : t("field_idsource_pdl"),
            value: formatIdSource(props.idsource, props.type),
        },
    ]
    if (props.owner?.company?.name) {
        lines.unshift({
            label: t("field_company"),
            value: props.owner.company.name,
        })
    }
    return lines
})

// ================================
// HANDLERS
// ================================

function onBack(): void {
    emit("back")
}

function onGenerate(): void {
    if (!getCanSubmit.value) return
    fetchSend()
}
</script>

<template>
    <div class="source-invitation-extend flex flex-col gap-4">
        <header class="w-full text-center flex flex-col gap-2">
            <h2 class="text-lg font-semibold text-gray-700 text-center">
                {{ t("title") }}
            </h2>
            <p class="text-sm text-gray-600">
                {{ t("subtitle") }}
            </p>
            <p class="text-sm text-gray-500">
                {{ t("explain") }}
            </p>
        </header>

        <BaseSummary :lines="getFields" variant="grey" />

        <BaseMessage
            v-if="!getCanSubmit && !isFetching"
            severity="warn"
            :closable="false"
            class="w-full"
        >
            {{ t("missing_user_id") }}
        </BaseMessage>

        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="t('back')"
                severity="secondary"
                :disabled="isFetching"
                class="min-w-[10rem] w-full md:w-auto"
                @click="onBack"
            />
            <BaseButton
                :label="t('generate')"
                :loading="isFetching"
                :disabled="!getCanSubmit || isFetching"
                class="min-w-[10rem] w-full md:w-auto"
                @click="onGenerate"
            />
        </div>

        <BaseMessage
            v-if="hasError && getErrorMessage"
            severity="error"
            :closable="false"
            class="w-full"
        >
            {{ getErrorMessage }}
        </BaseMessage>
    </div>
</template>
